import {
  IStoreGetters,
  IStatement,
  IResult,
  IPartyStatement,
  IShootoutStatement,
} from "./interfaces";
import { computed } from "vue";
import { useGetters } from "vuex-composition-helpers";

import { useStore } from "vuex";
import { useRoute } from "vue-router";

import { parties, dictionary } from "./constants";
import { statementTitleToString, slugify } from "./helpers";

export const useAmountSkipped = () => {
  const { user } = useGetters<IStoreGetters>(["user"]);
  return computed((): number => {
    return user.value.choices.reduce((total: number, choice: any) => {
      return total + (choice.choice === "skip" ? 1 : 0);
    }, 0);
  });
};

export const useMaxScore = () => {
  const { user } = useGetters<IStoreGetters>(["user"]);
  return computed((): number => {
    let maxScore = 0;

    if (user.value.priorityStatements && user.value.priorityStatements.length) {
      maxScore += user.value.priorityStatements.length;
    }

    user.value.choices.forEach((statement) => {
      if (statement.choice !== "skip") maxScore++;
    });

    return maxScore;
  });
};

export const useSetDocumentTitle = () => {
  const route = useRoute();
  const store = useStore();
  return function () {
    let title = "";

    if (route.name === "statement" || route.name === "shootout-statement") {
      title = `${dictionary.app.title} | ${route.name === "shootout-statement" ? dictionary.shootoutStatement.tabTitle : dictionary.statement.tabTitle} ${route.params.id} | ${statementTitleToString((store.getters.statement(JSON.parse(route.params.id.toString())) as IStatement).title)}`;
    } else if (route.meta.title) {
      title = `${dictionary.app.title} | ${route.meta.title}`;
    } else {
      title = `${dictionary.app.title}`;
    }
    try {
      document.getElementsByTagName("title")[0].innerHTML = title
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace(" & ", " &amp; ");
    } catch (Exception) {
      // ignore
    }

    document.title = title;
  };
};

export const useShareLink = () => {
  const route = useRoute();
  const store = useStore();

  return function (
    platform: "facebook" | "mastadon" | "bluesky" | "linkedin" | "whatsapp",
  ): string {
    let linkToModify: string = "";

    if (route.name === "statement") {
      if (dictionary.statement.share) {
        switch (platform) {
          case "facebook":
            linkToModify = dictionary.statement.share.facebookWithStatement;
            break;
          case "mastadon":
            linkToModify = dictionary.statement.share.mastadonWithStatement;
            break;
          case "bluesky":
            linkToModify = dictionary.statement.share.blueskyWithStatement;
            break;
          case "linkedin":
            linkToModify = dictionary.statement.share.linkedinWithStatement;
            break;
          case "whatsapp":
            linkToModify = dictionary.statement.share.whatsappWithStatement;
            break;
        }
      }

      linkToModify = linkToModify.replace(
        "[shareStatementUrl]",
        `${window.location.host}?${slugify(dictionary.statement.tabTitle)}=${store.getters.statement().id + 1}`,
      );
      linkToModify = linkToModify.replace(
        "[statementTitle]",
        store.getters.statement().title,
      );
    } else {
      switch (platform) {
        case "facebook":
          linkToModify = dictionary.app.share.facebook;
          break;
        case "mastadon":
          linkToModify = dictionary.app.share.mastadon;
          break;
        case "bluesky":
          linkToModify = dictionary.app.share.bluesky;
          break;
        case "linkedin":
          linkToModify = dictionary.app.share.linkedin;
          break;
        case "whatsapp":
          linkToModify = dictionary.app.share.whatsapp;
          break;
      }

      linkToModify = linkToModify.replace(
        "[appUrl]",
        `${window.location.host}`,
      );
    }

    return linkToModify;
  };
};

export const useResults = () => {
  const maxScore = useMaxScore();
  const route = useRoute();
  const store = useStore();
  const { user, statements } = useGetters<IStoreGetters>([
    "user",
    "statements",
  ]);
  return function (onlySelected: boolean): IResult[] {
    if (
      route.name !== "select-analytics" &&
      route.name !== "results" &&
      route.name !== "choices-overview"
    )
      return [];

    let results: IResult[] = [];
    let savedResults = store.getters.savedResults;

    if (savedResults) {
      if (
        JSON.stringify(user.value.choices) === savedResults.userChoicesString
      ) {
        results = savedResults.results;
      } else {
        store.dispatch("setSavedResults", false);

        savedResults = false;
      }
    }

    if (!results.length) {
      parties
        .filter((party: any) => {
          return party.participates;
        })
        .forEach((party: any) => {
          let percentage = 0;

          if (party.statements && party.statements.length) {
            Object.values(statements.value).forEach((statement) => {
              const userChoice = user.value.choices.filter((uStatement) => {
                return uStatement.originalStatementId === statement.id;
              })[0].choice;
              const partyPosition = (
                party.statements as IPartyStatement[]
              ).filter((pStatement) => {
                return pStatement.id === statement.id;
              })[0].position;
              const isDoubled = user.value.priorityStatements
                ? user.value.priorityStatements.indexOf(statement.id) !== -1
                : false;

              if (userChoice !== "skip" && userChoice === partyPosition) {
                percentage += ((isDoubled ? 2 : 1) / maxScore.value) * 100;
              }
            });
          }

          results.push({
            party,
            percentage: Math.round(percentage),
          });
        });

      results.sort((a, b) => {
        if (a.percentage === b.percentage) return 0;
        if (a.percentage > b.percentage) return -1;

        return 1;
      });
    }

    if (!savedResults) {
      store.dispatch("setSavedResults", {
        userChoicesString: JSON.stringify(user.value.choices),
        results,
      });
    }

    if (onlySelected) {
      results = results.filter((result) => {
        let partyId = -1;

        parties.forEach((party: any) => {
          if (JSON.stringify(party) === JSON.stringify(result.party))
            partyId = party.id;
        });

        return (
          partyId !== -1 &&
          (user.value.selectedParties as number[]).indexOf(partyId) !== -1
        );
      });
    }

    return results;
  };
};

// return the current shootoutstatements
// regardless of parties, as a computed value

export const useShootoutStatements = () => {
  const store = useStore();
  return computed(function (): IShootoutStatement[] {
    return store.getters.shootoutStatements();
  });
};
